import { useEffect, useRef, useState } from 'react'

type UseShopHorizontalScrollReturnType = {
  containerRef: React.RefObject<HTMLDivElement>
  canScrollToLeft: boolean
  canScrollToRight: boolean
  handleScrollLeft: () => void
  handleScrollRight: () => void
}

const SCROLL_STEP = 290

/*
 * This hook detect for the referenced element if user can scroll to the left or to the right.
 */
const useShopHorizontalScroll = (): UseShopHorizontalScrollReturnType => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [canScrollToLeft, setCanScrollToLeft] = useState(false)
  const [canScrollToRight, setCanScrollToRight] = useState(false)

  useEffect(() => {
    const container = containerRef.current

    const handleScroll = (): void => {
      if (container) {
        const scrollLeft = container.scrollLeft
        const maxScrollLeft = container.scrollWidth - container.clientWidth
        setCanScrollToLeft(scrollLeft > 0)
        setCanScrollToRight(scrollLeft < maxScrollLeft)
      }
    }

    const handleResize = (): void => {
      if (container) {
        /* Check if content width exceeds container width */
        const contentFits = container.scrollWidth <= container.clientWidth
        /* Since the horizontal scroll is initialy rendered at the beginning , the canScrollToLeft is set to false */
        setCanScrollToLeft(false)
        /* Show right arrow if content doesnt fit */
        setCanScrollToRight(!contentFits)
      }
    }

    if (container) {
      container.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleResize)
      /* This will do initial check for arrows */
      handleResize()
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [])

  const handleScrollLeft = (): void => {
    if (containerRef.current) {
      const container = containerRef.current
      container.scrollTo({
        left: container.scrollLeft - SCROLL_STEP,
        behavior: 'smooth',
      })
    }
  }

  const handleScrollRight = (): void => {
    if (containerRef.current) {
      const container = containerRef.current
      container.scrollTo({
        left: container.scrollLeft + SCROLL_STEP,
        behavior: 'smooth',
      })
    }
  }

  return {
    containerRef,
    canScrollToLeft,
    canScrollToRight,
    handleScrollLeft,
    handleScrollRight,
  }
}

export default useShopHorizontalScroll
